import React, { useEffect, useRef, useState } from 'react';
import { RhythmEpisode } from '../types';
import { SignalWindow } from '../signal';
import { BeatMarker } from '../review';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Info, Move, SkipBack, SkipForward, Sliders, ZoomIn, ZoomOut } from 'lucide-react';

/**
 * Nominal CSS pixels per millimetre (96 dpi). Physical size on a given monitor varies, so the strip
 * is standard-*proportioned* — 25 mm/s against 10 mm/mV, grid at 0.04 s and 0.1 mV — rather than
 * guaranteed to measure 25 mm per second with a ruler. Print strips (M2) are exact.
 */
const PX_PER_MM = 96 / 25.4;
const GRID_MAX_SECONDS = 30;
const LANE_HEIGHT = 170;

/** `classify_pause`'s own criterion (`max_pause_s >= 3.0`, §6.4), so the viewer measures what the
 *  engine decided on rather than a threshold of its own invention. */
const PAUSE_CRITERION_MS = 3000;

const CLASS_TINT: Record<string, string> = {
  afib: 'rgba(168, 85, 247, 0.16)',
  vt: 'rgba(244, 63, 94, 0.22)',
  possible_vf: 'rgba(244, 63, 94, 0.22)',
  pause: 'rgba(245, 158, 11, 0.18)',
};

const BEAT_COLOUR: Record<string, string> = {
  N: '#94a3b8', V: '#fb7185', S: '#fbbf24', A: '#475569', Q: '#a78bfa', P: '#38bdf8',
};

export interface ClockInfo {
  wearStartIso: string;
  timeZone: string;
}

/** Wall-clock time at the patient's location — the time a clinician and the diary speak in. */
export function wallClock(clock: ClockInfo, seconds: number, withDate = false): string {
  const t = new Date(new Date(clock.wearStartIso).getTime() + seconds * 1000);
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: clock.timeZone,
    ...(withDate ? { day: '2-digit', month: 'short' } : {}),
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(t);
}

const HR_STRIP_HEIGHT = 64;

/**
 * Instantaneous rate for the beats currently in view, from the RR intervals already fetched for
 * beat labelling (§ real-time HR request, 2026-09-14) — no new data needed. Complements the
 * per-beat RR numbers already drawn on the main canvas at close zoom: this is the shape of rate
 * *across* the strip, which the numbers alone don't give you at a glance.
 *
 * Always renders its slot, even with no data — a panel that disappears when you zoom out past
 * the beat-marker threshold made everything below it jump, which read as broken rather than
 * "nothing to show yet" (2026-09-14, from real use).
 */
/**
 * Widest window for which per-beat markers are fetched at all.
 *
 * Exported so `ReviewWorkspace` gates its fetch on the same number this panel explains. They were
 * two literals, and the panel's empty-state message quoted its own without knowing whether the
 * fetch had actually been skipped.
 */
export const BEAT_MARKER_MAX_SPAN_SECONDS = 120;

const HrStrip: React.FC<{ beatMarkers: BeatMarker[]; startSeconds: number; spanSeconds: number; clock: ClockInfo }> = ({
  beatMarkers, startSeconds, spanSeconds, clock,
}) => {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const [hoverX, setHoverX] = useState<number | null>(null);
  const rafId = useRef<number | null>(null);
  useEffect(() => () => { if (rafId.current !== null) cancelAnimationFrame(rafId.current); }, []);
  // Bounded to plausible RR intervals, the same 200-6000 ms window the engine applies everywhere
  // it turns an interval into a rate (DESIGN.md §13.6 E20). Without it this strip drew *detection
  // errors* as heart rates: a real 101-hour study reported "HR 15-1128 - mean 337" here while the
  // statistics panel, which does bound, said max 299 for the same recording. Two numbers for one
  // patient, one of them physiologically impossible, on the same screen.
  //
  // An interval outside the window is not a slow or fast heart, it is a missed or doubled beat —
  // so it is excluded rather than clamped. Clamping would invent a 300 bpm reading where the truth
  // is "we do not know what happened here".
  const points = beatMarkers
    .filter((b) => b.rr_ms && b.rr_ms >= 200 && b.rr_ms <= 6000)
    .map((b) => ({ t: b.t, bpm: 60000 / (b.rr_ms as number) }));
  const bpms = points.map((p) => p.bpm);
  const lo = bpms.length ? Math.min(...bpms) : 0;
  const hi = bpms.length ? Math.max(...bpms) : 0;
  // Nice round gridlines rather than the exact min/max, so the scale reads at a glance.
  const gridLo = Math.max(0, Math.floor(lo / 10) * 10 - 10);
  const gridHi = Math.ceil(hi / 10) * 10 + 10;
  const gridSpan = Math.max(gridHi - gridLo, 1);
  // A fixed 20 bpm step meant a wide-ranging window (an arrhythmia event is exactly when rate
  // swings most) packed 8+ labels into a 64px-tall strip, which rendered as an unreadable vertical
  // smear of overlapping digits — reported as "vertical lines... they're not lines but numbers...
  // an alignment issue" (2026-09-14, from real use). Pick a step that keeps roughly 3 gridlines
  // regardless of range instead.
  const gridStep = [10, 20, 25, 50, 100, 200].find((step) => gridSpan / step <= 4) ?? 200;

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const width = rect.width;
    const height = rect.height;
    ctx.fillStyle = '#090d16';
    ctx.fillRect(0, 0, width, height);
    if (points.length < 2) {
      // Three different situations produced one message, which asserted the first of them
      // regardless (2026-09-21, reported against a 10 s window). The second is the one that
      // matters clinically: "the engine found no beats here" is the *evidence* for a pause or an
      // asystole finding, and telling a reviewer to zoom in — on a strip where QRS complexes are
      // plainly visible — hides exactly the thing they need to judge that call.
      const message =
        spanSeconds > BEAT_MARKER_MAX_SPAN_SECONDS
          ? `Zoom to under ${Math.round(BEAT_MARKER_MAX_SPAN_SECONDS / 60)} minutes to see rate`
          : beatMarkers.length === 0
            ? 'No beats detected in this window by the engine'
            : 'No usable rate here — the intervals are outside 200–6000 ms';
      ctx.fillStyle = '#475569';
      ctx.font = '10px JetBrains Mono, monospace';
      ctx.fillText(message, 8, height / 2 + 3);
      return;
    }

    const xOf = (t: number) => ((t - startSeconds) / spanSeconds) * width;
    const yOf = (bpm: number) => height - 4 - ((bpm - gridLo) / gridSpan) * (height - 8);

    // Reference lines, labelled — the min/max text alone didn't say what the rate was *at* a
    // given point (2026-09-14, from real use).
    ctx.font = '9px JetBrains Mono, monospace';
    for (let ref = Math.ceil(gridLo / gridStep) * gridStep; ref <= gridHi; ref += gridStep) {
      const y = yOf(ref);
      ctx.strokeStyle = 'rgba(148,163,184,0.14)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(width, y);
      ctx.stroke();
      ctx.fillStyle = '#64748b';
      ctx.fillText(String(ref), 2, y - 2);
    }

    ctx.strokeStyle = '#34d399';
    ctx.lineWidth = 1.3;
    ctx.beginPath();
    points.forEach((p, i) => {
      const x = xOf(p.t);
      const y = yOf(p.bpm);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();
    ctx.fillStyle = '#34d399';
    points.forEach((p) => {
      ctx.beginPath();
      ctx.arc(xOf(p.t), yOf(p.bpm), 1.5, 0, Math.PI * 2);
      ctx.fill();
    });

    // Hover readout: nearest beat's exact rate and time, not just the aggregate.
    if (hoverX !== null) {
      let nearest = points[0];
      let bestDist = Infinity;
      for (const p of points) {
        const d = Math.abs(xOf(p.t) - hoverX);
        if (d < bestDist) { bestDist = d; nearest = p; }
      }
      const x = xOf(nearest.t);
      ctx.strokeStyle = 'rgba(255,255,255,0.35)';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#f8fafc';
      ctx.font = '10px JetBrains Mono, monospace';
      const label = `${Math.round(nearest.bpm)} bpm · ${wallClock(clock, nearest.t)}`;
      ctx.fillText(label, Math.min(x + 5, width - 120), 11);
    }

    // Aggregate overlaid on the graph itself, top-right — a separate label column beside the
    // canvas meant this and the ECG canvas below it were never actually the same width (2026-09-14,
    // from real use: "i would want the width of the heart rate graph and the ecg grid to match
    // exactly"). One component, not two: same bordered panel, same width, the summary drawn as part
    // of the same drawing instead of sitting next to it.
    if (bpms.length >= 2) {
      const mean = Math.round(bpms.reduce((a, b) => a + b, 0) / bpms.length);
      const text = `HR ${Math.round(lo)}–${Math.round(hi)} · mean ${mean}`;
      ctx.font = '10px JetBrains Mono, monospace';
      const w = ctx.measureText(text).width;
      ctx.fillStyle = 'rgba(9, 13, 22, 0.75)';
      ctx.fillRect(width - w - 12, 2, w + 8, 13);
      ctx.fillStyle = '#34d399';
      ctx.fillText(text, width - w - 8, 11);
    }
  }, [points, startSeconds, spanSeconds, hoverX, gridLo, gridHi, gridSpan, gridStep, clock, bpms, lo, hi]);

  return (
    <div className="relative overflow-hidden rounded-xl border border-slate-800 bg-slate-950">
      <canvas
        ref={ref}
        style={{ height: HR_STRIP_HEIGHT }}
        className="block w-full cursor-crosshair"
        onMouseMove={(e) => {
          const x = e.clientX - e.currentTarget.getBoundingClientRect().left;
          if (rafId.current !== null) return;
          rafId.current = requestAnimationFrame(() => { rafId.current = null; setHoverX(x); });
        }}
        onMouseLeave={() => setHoverX(null)}
      />
    </div>
  );
};

interface WaveformViewerProps {
  /** One window per channel, drawn as stacked lanes on a shared time axis. */
  waveforms: SignalWindow[];
  channelLabels: string[];
  startSample: number;
  spanSamples: number;
  totalSamples: number;
  sampleRateHz: number;
  clock: ClockInfo;
  onWindowChange: (startSample: number, spanSamples: number) => void;
  episodes?: RhythmEpisode[];
  focusEpisodeId?: string | null;
  beatMarkers?: BeatMarker[];
  isLoading?: boolean;
}

export const WaveformViewer: React.FC<WaveformViewerProps> = ({
  waveforms, channelLabels, startSample, spanSamples, totalSamples, sampleRateHz, clock,
  onWindowChange, episodes = [], focusEpisodeId = null, beatMarkers = [], isLoading = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [gain, setGain] = useState<number>(10);
  const [showCalibrationHelp, setShowCalibrationHelp] = useState(false);
  const [hoverX, setHoverX] = useState<number | null>(null);
  const [gotoText, setGotoText] = useState('');
  const drag = useRef<{ x: number; start: number } | null>(null);

  // Raw mousemove can fire far more often than 60Hz, and every call used to go straight into
  // setHoverX/setWindow — each one a state update, each one a full canvas redraw (grid, every
  // sample in the window, beat markers, episode shading). That's real, measured UI lag while
  // dragging or even just hovering, not a guess — reported as "the lags on screens" (2026-09-14).
  // Collapsing to at most one update per animation frame is the standard fix for exactly this.
  const rafId = useRef<number | null>(null);
  const pendingClientX = useRef<number | null>(null);
  const scheduleMouseMove = (clientX: number) => {
    pendingClientX.current = clientX;
    if (rafId.current !== null) return;
    rafId.current = requestAnimationFrame(() => {
      rafId.current = null;
      const rect = canvasRef.current?.getBoundingClientRect();
      const x = pendingClientX.current;
      if (!rect || x === null) return;
      setHoverX(x - rect.left);
      if (drag.current) {
        const dx = x - drag.current.x;
        setWindow(drag.current.start - (dx / rect.width) * liveSpan);
      }
    });
  };
  useEffect(() => () => { if (rafId.current !== null) cancelAnimationFrame(rafId.current); }, []);

  // A drag or the slider can change the window many times a second. The grid and episode shading
  // below read `liveStart`/`liveSpan`, which update every event so panning feels immediate; the
  // parent (and the signal fetch it triggers) only hears about it ~100ms after movement pauses, via
  // `commitWindow`. Without this the actual trace visibly lagged the grid during a drag — found
  // 2026-09-14 from real use, worsened by the request pileup fixed in ReviewWorkspace's fetch effect.
  const [liveStart, setLiveStart] = useState(startSample);
  const [liveSpan, setLiveSpan] = useState(spanSamples);
  useEffect(() => {
    setLiveStart(startSample);
    setLiveSpan(spanSamples);
  }, [startSample, spanSamples]);
  const commitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (commitTimer.current) clearTimeout(commitTimer.current); }, []);

  const fs = sampleRateHz;
  const spanSeconds = liveSpan / fs;
  const startSeconds = liveStart / fs;
  const lanes = Math.max(1, channelLabels.length);

  const setWindow = (start: number, span = liveSpan) => {
    const clampedSpan = Math.max(Math.round(fs * 2), Math.min(totalSamples, Math.round(span)));
    const clampedStart = Math.max(0, Math.min(Math.round(start), totalSamples - clampedSpan));
    setLiveStart(clampedStart);
    setLiveSpan(clampedSpan);
    if (commitTimer.current) clearTimeout(commitTimer.current);
    commitTimer.current = setTimeout(() => onWindowChange(clampedStart, clampedSpan), 100);
  };
  const pan = (fraction: number) => setWindow(liveStart + fraction * liveSpan);
  const zoom = (factor: number) => {
    const centre = liveStart + liveSpan / 2;
    const span = Math.max(Math.round(fs * 2), Math.min(totalSamples, Math.round(liveSpan * factor)));
    setWindow(centre - span / 2, span);
  };
  const setPaperSpeed = (mmPerSecond: number) => {
    const width = canvasRef.current?.clientWidth ?? 900;
    const centre = liveStart + liveSpan / 2;
    const span = (width / (mmPerSecond * PX_PER_MM)) * fs;
    setWindow(centre - span / 2, span);
  };

  const goTo = () => {
    // Accepts "D2 14:30:05" (study day and time), "14:30:05" (first matching time), or plain seconds.
    const text = gotoText.trim();
    let seconds: number | null = null;
    const dayTime = /^d(\d+)\s+(\d{1,2}):(\d{2})(?::(\d{2}))?$/i.exec(text);
    const hms = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(text);
    if (dayTime) {
      seconds = (Number(dayTime[1]) - 1) * 86400 + Number(dayTime[2]) * 3600 + Number(dayTime[3]) * 60 + Number(dayTime[4] ?? 0);
    } else if (hms) {
      seconds = Number(hms[1]) * 3600 + Number(hms[2]) * 60 + Number(hms[3] ?? 0);
    } else if (/^\d+(\.\d+)?$/.test(text)) {
      seconds = Number(text);
    }
    if (seconds !== null) setWindow(seconds * fs - liveSpan / 2);
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    const width = rect.width;
    const height = rect.height;
    const laneH = height / lanes;
    const xOf = (seconds: number) => ((seconds - startSeconds) / spanSeconds) * width;
    const pxPerMv = gain * PX_PER_MM;

    ctx.fillStyle = '#090d16';
    ctx.fillRect(0, 0, width, height);

    const labelled: { ep: RhythmEpisode; x1: number }[] = [];
    for (const ep of episodes) {
      const x1 = xOf(ep.onset_seconds);
      const x2 = xOf(ep.offset_seconds);
      if (x2 < 0 || x1 > width) continue;
      ctx.fillStyle = CLASS_TINT[ep.rhythm_class] ?? 'rgba(148, 163, 184, 0.12)';
      ctx.fillRect(Math.max(0, x1), 0, Math.min(width, x2) - Math.max(0, x1), height);
      if (ep.id === focusEpisodeId) {
        // Was a strokeRect box spanning min(x1)..max(x2) regardless of whether either edge was
        // actually in view — for a long episode (sustained AFib can run for hours) that meant a
        // box clamped flush against the canvas edge, which reads as a grabbable resize handle
        // (edge-of-container handles are exactly how resizing conventionally looks), and IS
        // draggable in the sense that dragging anywhere on the canvas pans — so grabbing that edge
        // visibly "resized" the box as a side effect of panning changing how much of the episode
        // is in view. Reported as "it IS interactive... changes the graph... cant be left as is"
        // (2026-09-14). Fixed at the source: only draw a boundary where the actual edge is
        // genuinely interior (margin so it never sits flush against the canvas edge), as an
        // independent tick per edge rather than one box spanning both — a tick with no edge flush
        // against the container has nothing that reads as a handle.
        const margin = 4;
        const onsetVisible = x1 > margin && x1 < width - margin;
        const offsetVisible = x2 > margin && x2 < width - margin;
        const tick = (x: number, flagLeft: boolean) => {
          ctx.strokeStyle = '#22d3ee';
          ctx.lineWidth = 2;
          ctx.setLineDash([5, 4]);
          ctx.beginPath();
          ctx.moveTo(x, 1);
          ctx.lineTo(x, height - 1);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = '#22d3ee';
          ctx.beginPath();
          ctx.moveTo(x, 1);
          ctx.lineTo(x + (flagLeft ? -7 : 7), 1);
          ctx.lineTo(x, 9);
          ctx.closePath();
          ctx.fill();
        };
        if (onsetVisible) tick(x1, false);
        if (offsetVisible) tick(x2, true);
        if (!onsetVisible && !offsetVisible) {
          ctx.fillStyle = '#67e8f9';
          ctx.font = '10px JetBrains Mono, monospace';
          ctx.fillText(`◄ within ${ep.rhythm_class.toUpperCase()} — boundary out of view ►`, 8, 16);
        }
        // A light wash on top of the class tint so the focused episode visibly stands out from
        // any other episode overlapping the same view, not just via the ticks at its edges —
        // "when toggling between events... we need a clearer indication where the arrhythmia is
        // marked" (2026-09-14).
        ctx.fillStyle = 'rgba(34, 211, 238, 0.10)';
        ctx.fillRect(Math.max(0, x1), 0, Math.min(width, x2) - Math.max(0, x1), height);
      }
      labelled.push({ ep, x1 });
    }

    // Labels go in a second pass, after every band is tinted, for two reasons.
    //
    // First, a later episode's tint used to paint straight over an earlier episode's label.
    //
    // Second, and the visible bug: each label was drawn at `max(6, x1 + 4)`, so *every* episode
    // starting at or before the left edge clamped to the same x and they all overprinted each
    // other into an unreadable smear (observed as "AEDBT2R040s0s" where three labels stacked).
    // Clamping is right for one off-screen-left episode and wrong for several, so the fix is to
    // place labels only where they actually fit and drop the ones that do not — a tinted band with
    // no label still reads correctly, whereas overlapping glyphs read as nothing at all.
    ctx.font = '11px JetBrains Mono, monospace';
    const LABEL_GAP = 6;
    const placed: { left: number; right: number }[] = [];
    // The focused episode is placed first so it always wins the space it needs: it is the one the
    // reviewer is actually looking at, and losing its label to an arbitrary neighbour would be the
    // worst of the possible drops.
    const order = [...labelled].sort((a, b) => {
      if (a.ep.id === focusEpisodeId) return -1;
      if (b.ep.id === focusEpisodeId) return 1;
      return a.x1 - b.x1;
    });
    for (const { ep, x1 } of order) {
      const text = `${ep.rhythm_class.toUpperCase()} ${ep.duration_seconds.toFixed(1)}s`;
      const left = Math.max(6, x1 + 4);
      const right = left + ctx.measureText(text).width;
      if (right > width) continue;
      if (placed.some((p) => left < p.right + LABEL_GAP && right + LABEL_GAP > p.left)) continue;
      ctx.fillStyle = ep.id === focusEpisodeId ? '#67e8f9' : '#e2e8f0';
      ctx.fillText(text, left, height - 8);
      placed.push({ left, right });
    }

    for (let lane = 0; lane < lanes; lane += 1) {
      const top = lane * laneH;
      const centreY = top + laneH / 2;
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, top, width, laneH);
      ctx.clip();

      if (spanSeconds <= GRID_MAX_SECONDS) {
        ctx.lineWidth = 1;
        const drawTime = (step: number, style: string) => {
          ctx.strokeStyle = style;
          for (let t = Math.ceil(startSeconds / step) * step; t <= startSeconds + spanSeconds; t += step) {
            ctx.beginPath();
            ctx.moveTo(xOf(t), top);
            ctx.lineTo(xOf(t), top + laneH);
            ctx.stroke();
          }
        };
        const drawAmp = (stepMv: number, style: string) => {
          ctx.strokeStyle = style;
          for (let k = 0; k * stepMv * pxPerMv <= laneH / 2; k += 1) {
            for (const y of [centreY - k * stepMv * pxPerMv, centreY + k * stepMv * pxPerMv]) {
              ctx.beginPath();
              ctx.moveTo(0, y);
              ctx.lineTo(width, y);
              ctx.stroke();
            }
          }
        };
        if ((0.04 / spanSeconds) * width >= 3) drawTime(0.04, 'rgba(6, 182, 212, 0.07)');
        drawTime(0.2, 'rgba(6, 182, 212, 0.18)');
        drawAmp(0.1, 'rgba(6, 182, 212, 0.07)');
        drawAmp(0.5, 'rgba(6, 182, 212, 0.18)');
      }

      const w = waveforms[lane];
      if (w?.samples && w.samples.length > 0) {
        ctx.strokeStyle = '#22d3ee';
        ctx.lineWidth = 1.2;
        const stepX = width / w.samples.length;
        ctx.beginPath();
        w.samples.forEach((v, i) => {
          const y = centreY - v * pxPerMv;
          if (i === 0) ctx.moveTo(0, y);
          else ctx.lineTo(i * stepX, y);
        });
        ctx.stroke();
      } else if (w?.mins && w.maxs) {
        // An envelope is a summary of the span, drawn as a band so it never reads as a trace.
        const stepX = width / w.mins.length;
        ctx.fillStyle = 'rgba(34, 211, 238, 0.55)';
        for (let i = 0; i < w.mins.length; i += 1) {
          const yMax = centreY - w.maxs[i] * pxPerMv;
          const yMin = centreY - w.mins[i] * pxPerMv;
          ctx.fillRect(i * stepX, yMax, Math.max(stepX, 1), Math.max(yMin - yMax, 1));
        }
      }

      // A tick at every detected beat, on **every** lane — not just a letter at the top of lane 0.
      //
      // Asked directly, of a reported 4.8 s pause: "i dont see a missed beat... all the beats look
      // evenly spaced to me. am i missing something" (2026-09-18). The letters were the only
      // indication of where the engine thought beats were, they were drawn on one lane, and a
      // glyph floating above the trace cannot be lined up against the complexes below it. So the
      // one question a reviewer must be able to answer about any RR-based finding — *did the
      // engine see the beats I can see?* — had no answer on screen.
      //
      // With ticks, a real pause reads as a stretch of trace with no ticks on it, and a false one
      // reads as QRS complexes with no ticks on them. Both are then a glance rather than an appeal
      // to authority, which matters twice over given §13.6 E4: the engine's beat series is exactly
      // the thing that degrades on noisy signal.
      if (spanSeconds <= 120) {
        beatMarkers.forEach((beat) => {
          const x = xOf(beat.t);
          if (x < 0 || x > width) return;
          ctx.strokeStyle = BEAT_COLOUR[beat.label] ?? '#94a3b8';
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(x, top + 4);
          ctx.lineTo(x, top + 13);
          ctx.stroke();
        });
      }

      if (lane === 0 && spanSeconds <= 120) {
        ctx.font = '10px JetBrains Mono, monospace';
        // Right edge of the last RR label drawn, so the next one can be skipped rather than
        // printed on top of it (2026-09-21). Beat spacing shrinks with rate while the label
        // stays ~24px wide, so above roughly 170 bpm consecutive labels collide — on a real
        // study with double-detected beats this rendered as an unreadable run of digits
        // ("70139127"), which looks like corrupt data rather than two numbers overlapping.
        //
        // Dropping labels is the right trade: the RR *markers* are still all drawn, and a
        // number you cannot read is worth less than the gap that tells you to zoom in.
        let lastRrLabelRight = -Infinity;
        beatMarkers.forEach((beat, i) => {
          const x = xOf(beat.t);
          if (x < 0 || x > width) return;
          ctx.fillStyle = BEAT_COLOUR[beat.label] ?? '#94a3b8';
          ctx.fillText(beat.label, x - 3, top + 24);
          if (spanSeconds <= 20 && beat.rr_ms && i > 0) {
            const text = String(Math.round(beat.rr_ms));
            const labelX = (xOf(beatMarkers[i - 1].t) + x) / 2 - 10;
            const labelWidth = ctx.measureText(text).width;
            if (labelX >= lastRrLabelRight + 4) {
              ctx.fillStyle = '#64748b';
              ctx.fillText(text, labelX, top + 36);
              lastRrLabelRight = labelX + labelWidth;
            }
          }
        });

        // Any gap long enough to be a pause gets measured on screen, with its endpoints marked.
        // `classify_pause` keys on `max_pause_s >= 3.0` (§6.4), so this draws the engine's own
        // criterion where it can be checked against the trace rather than read from a sidebar.
        // Deliberately drawn from the same RR values the classifier used: if the bracket looks
        // wrong, the finding *is* wrong, and that is the point.
        beatMarkers.forEach((beat, i) => {
          if (i === 0 || !beat.rr_ms || beat.rr_ms < PAUSE_CRITERION_MS) return;
          const x2 = xOf(beat.t);
          const x1 = xOf(beatMarkers[i - 1].t);
          if (x2 < 0 || x1 > width) return;
          const y = top + 48;
          ctx.strokeStyle = '#f59e0b';
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(x1, y - 5); ctx.lineTo(x1, y + 5);
          ctx.moveTo(x1, y); ctx.lineTo(x2, y);
          ctx.moveTo(x2, y - 5); ctx.lineTo(x2, y + 5);
          ctx.stroke();
          ctx.fillStyle = '#fbbf24';
          ctx.font = 'bold 11px JetBrains Mono, monospace';
          const text = `${(beat.rr_ms / 1000).toFixed(1)} s — no beat detected`;
          ctx.fillText(text, Math.max(2, (x1 + x2) / 2 - ctx.measureText(text).width / 2), y - 9);
          ctx.font = '10px JetBrains Mono, monospace';
        });
      }

      // Top-right of the lane, not bottom-left: the episode class/duration label (drawn above,
      // spanning the whole canvas) sits at the very bottom, and for the *last* lane that y almost
      // exactly coincided with the channel label's old position — "ECG_CH_B... overlapped with
      // some other text... unreadable" (2026-09-14, from real use). Top-right stays clear of that,
      // the beat-letter/RR-interval text near the top-left on lane 0, and the calibration pulse.
      ctx.fillStyle = '#10b981';
      ctx.font = '10px JetBrains Mono, monospace';
      ctx.textAlign = 'right';
      ctx.fillText(channelLabels[lane] ?? '', width - 8, top + 14);
      ctx.textAlign = 'left';
      // 1 mV calibration pulse per lane.
      ctx.strokeStyle = '#10b981';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(8, centreY);
      ctx.lineTo(14, centreY);
      ctx.lineTo(14, centreY - pxPerMv);
      ctx.lineTo(14 + 5 * PX_PER_MM, centreY - pxPerMv);
      ctx.lineTo(14 + 5 * PX_PER_MM, centreY);
      ctx.lineTo(24 + 5 * PX_PER_MM, centreY);
      ctx.stroke();

      // The scale, printed beside the pulse it explains (§13.6 U13, §7.8).
      //
      // Every printed ECG carries its sweep speed and gain, because without them the trace cannot
      // be interpreted: the same complex at 50 mm/s and 25 mm/s is two different widths on paper,
      // and QRS width is a diagnostic criterion. Ours had them in the *UI controls* only — so a
      // strip exported into a report (§8) or screenshotted into an email arrived carrying a
      // calibration pulse whose meaning had been left behind in a toolbar. A pulse without its
      // scale is decoration.
      //
      // Measured rather than assumed: the horizontal figure is what this window actually works out
      // at, not the button that was last pressed. Zooming with the scroll wheel or the magnifier
      // changes the span without touching the speed control, so the button and the truth part
      // company constantly — and the number on a clinical record has to be the true one.
      ctx.font = '9px JetBrains Mono, monospace';
      ctx.fillStyle = '#10b981';
      ctx.textAlign = 'left';
      const mmPerSecondNow = width / (spanSeconds * PX_PER_MM);
      const sweep = mmPerSecondNow >= 10 ? mmPerSecondNow.toFixed(0) : mmPerSecondNow.toFixed(1);
      ctx.fillText(`${sweep} mm/s · ${gain} mm/mV`, 8, centreY + pxPerMv + 12);
      ctx.restore();

      if (lane > 0) {
        ctx.strokeStyle = 'rgba(148,163,184,0.25)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(0, top);
        ctx.lineTo(width, top);
        ctx.stroke();
      }
    }

    if (isLoading && waveforms.length === 0) {
      ctx.fillStyle = '#94a3b8';
      ctx.font = '13px Inter, sans-serif';
      ctx.fillText('Loading waveform…', width / 2 - 60, height / 2);
    }

    if (hoverX !== null) {
      ctx.strokeStyle = 'rgba(255,255,255,0.35)';
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(hoverX, 0);
      ctx.lineTo(hoverX, height);
      ctx.stroke();
      ctx.setLineDash([]);
      const t = startSeconds + (hoverX / width) * spanSeconds;
      ctx.fillStyle = '#f8fafc';
      ctx.font = '11px JetBrains Mono, monospace';
      ctx.fillText(`${wallClock(clock, t)} · ${t.toFixed(2)} s`, Math.min(hoverX + 6, width - 170), 44);
    }
  }, [waveforms, channelLabels, gain, startSeconds, spanSeconds, episodes, focusEpisodeId, beatMarkers, isLoading, hoverX, lanes, clock]);

  const effectiveSpeed = (canvasRef.current?.clientWidth ?? 900) / (spanSeconds * PX_PER_MM);
  const maxStart = Math.max(0, totalSamples - liveSpan);
  const level = waveforms[0];
  const btn = 'flex items-center gap-1 rounded-lg border border-slate-800 bg-slate-900 px-2 py-1.5 text-slate-200 hover:bg-slate-800';

  return (
    // No panel chrome of its own: rendered inside the same bordered panel as OverviewLanes
    // (ReviewWorkspace) so the controls below visibly belong to both, not just the chart.
    //
    // Order is HR strip, then canvas, then controls (2026-09-14, from real use): the toolbar used
    // to sit above the canvas, which put the thing you're actually looking at below a row of
    // buttons you reach for less often than you look at the trace. HR now sits directly above the
    // canvas it describes rather than near the whole-week overview above this panel, which is a
    // different timescale entirely.
    <div className="space-y-3">
      <HrStrip beatMarkers={beatMarkers} startSeconds={startSeconds} spanSeconds={spanSeconds} clock={clock} />

      <div className="relative overflow-hidden rounded-xl border border-slate-800 bg-slate-950">
        <canvas
          ref={canvasRef}
          style={{ height: LANE_HEIGHT * lanes }}
          onMouseDown={(e) => { drag.current = { x: e.clientX, start: liveStart }; }}
          onMouseMove={(e) => scheduleMouseMove(e.clientX)}
          onMouseUp={() => { drag.current = null; }}
          onMouseLeave={() => { drag.current = null; pendingClientX.current = null; setHoverX(null); }}
          className="block w-full cursor-grab active:cursor-grabbing"
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        <div className="flex flex-wrap items-center gap-2">
          {/* Named in words, not left to the icon. Both groups are standard ECG calibration — but
              "mm/mV" and "mm/s" only read as gain and sweep speed if you already know the
              convention, and the icons alone communicated nothing (the Move glyph in particular
              reads as "pan"). Asked directly what these were for, 2026-09-18. */}
          <div
            className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900 px-2.5 py-1.5"
            title="Gain — vertical scale, in millimetres of trace per millivolt of signal. 10 mm/mV is the clinical standard; 20 enlarges a low-voltage recording, 5 keeps very tall complexes from overlapping."
          >
            <Sliders className="h-3.5 w-3.5 text-cyan-400" />
            <span className="font-semibold uppercase tracking-wide text-slate-500">Gain</span>
            {[5, 10, 20].map((g) => (
              <button key={g} onClick={() => setGain(g)}
                title={g === 10 ? '10 mm/mV — clinical standard' : `${g} mm/mV`}
                className={`rounded px-2 py-0.5 font-semibold ${gain === g ? 'bg-cyan-600 text-white' : 'text-slate-400 hover:text-white'}`}>
                {g} mm/mV
              </button>
            ))}
          </div>
          <div
            className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900 px-2.5 py-1.5"
            title="Sweep speed — horizontal scale, in millimetres of trace per second of recording. 25 mm/s is the clinical standard; 50 stretches time out for morphology, 12.5 compresses it to see more at once."
          >
            <Move className="h-3.5 w-3.5 text-emerald-400" />
            <span className="font-semibold uppercase tracking-wide text-slate-500">Speed</span>
            {[12.5, 25, 50].map((s) => (
              <button key={s} onClick={() => setPaperSpeed(s)}
                title={s === 25 ? '25 mm/s — clinical standard' : `${s} mm/s`}
                className={`rounded px-2 py-0.5 font-semibold ${Math.abs(effectiveSpeed - s) < 0.5 ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}>
                {s} mm/s
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowCalibrationHelp((v) => !v)}
            aria-expanded={showCalibrationHelp}
            title="What do these settings mean?"
            className={`flex items-center gap-1 rounded-lg border px-2 py-1.5 ${
              showCalibrationHelp
                ? 'border-cyan-700 bg-cyan-950 text-cyan-300'
                : 'border-slate-800 bg-slate-900 text-slate-400 hover:text-white'
            }`}
          >
            <Info className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <button className={btn} title="Start (Home)" onClick={() => setWindow(0)}><SkipBack className="h-3.5 w-3.5" /></button>
          <button className={btn} title="Back one window (Shift+←)" onClick={() => pan(-1)}><ChevronsLeft className="h-3.5 w-3.5" /></button>
          <button className={btn} title="Back half a window (←)" onClick={() => pan(-0.5)}><ChevronLeft className="h-3.5 w-3.5" /></button>
          <button className={btn} title="Forward half a window (→)" onClick={() => pan(0.5)}><ChevronRight className="h-3.5 w-3.5" /></button>
          <button className={btn} title="Forward one window (Shift+→)" onClick={() => pan(1)}><ChevronsRight className="h-3.5 w-3.5" /></button>
          <button className={btn} title="End (End)" onClick={() => setWindow(maxStart)}><SkipForward className="h-3.5 w-3.5" /></button>
          <button className={btn} title="Zoom in (Shift+Z)" onClick={() => zoom(0.5)}><ZoomIn className="h-3.5 w-3.5 text-cyan-400" /></button>
          <button className={btn} title="Zoom out (Z)" onClick={() => zoom(2)}><ZoomOut className="h-3.5 w-3.5 text-cyan-400" /></button>
          <input
            value={gotoText}
            onChange={(e) => setGotoText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && goTo()}
            placeholder="Go to D2 14:30:05"
            className="w-36 rounded-lg border border-slate-800 bg-slate-950 px-2 py-1.5 text-slate-200"
          />
        </div>
      </div>

      {showCalibrationHelp && (
        // Both settings are inherited wholesale from paper ECG printouts, which is why the units
        // are millimetres of something per unit of something else. Neither changes the data — they
        // change the ruler it is measured against, and that is the part worth saying out loud.
        <div className="space-y-3 rounded-xl border border-slate-800 bg-slate-900/60 p-4 text-xs text-slate-300">
          <div>
            <p className="font-semibold text-cyan-300">Gain — how tall (mm/mV)</p>
            <p className="text-slate-400">
              Millimetres of vertical trace per millivolt of signal. <strong>10 mm/mV is the
              clinical standard.</strong> Use 20 mm/mV to bring up a low-voltage recording, or
              5 mm/mV when complexes are tall enough to collide with the lane above.
            </p>
          </div>
          <div>
            <p className="font-semibold text-emerald-300">Sweep speed — how wide (mm/s)</p>
            <p className="text-slate-400">
              Millimetres of horizontal trace per second of recording. <strong>25 mm/s is the
              clinical standard.</strong> Use 50 mm/s to stretch time out when judging morphology
              (QRS width, P waves), or 12.5 mm/s to compress it and see more of the record at once.
            </p>
          </div>
          <div>
            <p className="font-semibold text-slate-200">Why the grid matters</p>
            <p className="text-slate-400">
              At the standard 25 mm/s and 10 mm/mV the grid becomes a measuring instrument: one
              small square is <span className="mono">0.04 s</span> wide and{' '}
              <span className="mono">0.1 mV</span> tall, one large square{' '}
              <span className="mono">0.20 s</span> and <span className="mono">0.5 mV</span>. That is
              what makes a QRS wider than three small squares (<span className="mono">0.12 s</span>)
              readable as "wide" at a glance. Change either setting and the grid rescales with it,
              so the squares always mean what they say.
            </p>
          </div>
          <div>
            <p className="font-semibold text-slate-200">The step at the left edge</p>
            <p className="text-slate-400">
              That green marker is the <strong>1 mV calibration pulse</strong>, drawn once per lane.
              It is the visual check that the gain is what it claims to be — at 10 mm/mV it stands
              exactly two large squares tall.
            </p>
          </div>
          <p className="text-[11px] text-slate-500">
            Sizes are standard-<em>proportioned</em> rather than guaranteed in physical millimetres:
            a monitor's true pixel pitch varies. Printed strips are exact.
          </p>
        </div>
      )}

      {/* No separate position slider: it was the least precise of three ways to do the same job
          (itself, dragging the canvas, and the overview's click-to-jump above it, §ReviewWorkspace
          layout 2026-09-14) — a 900px-wide native range input standing in for a multi-day
          recording is coarser than clicking the actual proportional timeline. Dragging the canvas
          covers fine panning; the overview (when present) and "Go to" cover jumping. */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-400 mono">
        <span>
          {wallClock(clock, startSeconds, true)} → {wallClock(clock, startSeconds + spanSeconds)} · {spanSeconds.toFixed(1)} s window
          {level && ` · L${level.level}${level.level === 0 ? ' raw' : ` envelope ×${level.factor}`}`}
        </span>
        <span>{level?.level === 0 ? 'Raw samples' : 'Min/max envelope — a summary, not a trace'} · drag to pan</span>
      </div>
    </div>
  );
};
